/**
 * components/landing/home/PlatformArchitecture.jsx — Phase 2.2 · the
 * signature Interactive Platform Architecture section of the Kalki Ai Cloud
 * marketing home.
 *
 * A three-panel enterprise showcase that answers the question:
 *     "How does Kalki Ai Cloud actually work?"
 *
 * Layout
 * ──────
 *   Desktop (xl+):  [ Left info panel | Center architecture | Right preview ]
 *   Tablet (lg):    Center architecture full-width, info + preview stacked below
 *   Mobile (<lg):   Vertical stack — architecture (constrained), info card,
 *                   swipeable module list, preview.
 *
 * State
 * ─────
 *   viewModeId : 'overview' | 'ai' | 'trading' | 'ops' | 'wl'
 *   activeId   : selected module id | null
 *   hoverId    : hovered module id  | null
 *
 * Design tokens: uses the same GlassCard + gradient palette as the rest
 * of the marketing site — no dependencies outside the design-system.
 *
 * Motion respects `prefers-reduced-motion` — the SVG canvas passes the
 * flag downstream and skips the rotating rings + moving packets.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import Link from '@/shims/NextLink'
import { RotateCcw, Sparkles, ArrowRight } from 'lucide-react'

import { Container, Eyebrow, SectionHeading, GhostButton } from '../design-system'
import { useInView } from './_shared'
import ArchitectureCanvas from './architecture/ArchitectureCanvas'
import InfoPanel          from './architecture/InfoPanel'
import PreviewPanel       from './architecture/PreviewPanel'
import ViewModeSwitcher   from './architecture/ViewModeSwitcher'
import { VIEW_MODES, MODULES, getModule, TONE_COLORS } from './architecture/data'

const PlatformArchitecture = () => {
  const ref = useRef(null)
  const visible = useInView(ref, { rootMargin: '0px 0px -120px 0px' })

  const [viewModeId, setViewModeId] = useState('overview')
  const [activeId,   setActiveId]   = useState(null)
  const [hoverId,    setHoverId]    = useState(null)
  const [reducedMotion, setReducedMotion] = useState(false)

  const viewMode = useMemo(
    () => VIEW_MODES.find((v) => v.id === viewModeId) || VIEW_MODES[0],
    [viewModeId],
  )

  // Watch reduced-motion preference
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReducedMotion(mq.matches)
    const listener = () => setReducedMotion(mq.matches)
    mq.addEventListener?.('change', listener)
    return () => mq.removeEventListener?.('change', listener)
  }, [])

  // Deep-link support — read hash on mount, and update as user browses.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const initial = (window.location.hash || '').match(/^#arch-(.+)$/)
    if (initial && getModule(initial[1])) {
      setActiveId(initial[1])
      // Deep-link visitors expect the page to scroll to the section they
      // linked to (not sit at the top with an invisible module selected).
      // Wait one frame so the section has laid out, then scroll it into
      // view with a small header offset.
      requestAnimationFrame(() => {
        try {
          const el = document.getElementById('platform-architecture')
          el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        } catch (_e) { /* no-op */ }
      })
    }
  }, [])
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (activeId) {
      const url = new URL(window.location.href)
      url.hash = `#arch-${activeId}`
      history.replaceState(null, '', url)
    }
  }, [activeId])

  // Auto-select a "hero" module when the user changes view mode and
  // no module is currently selected — makes the section feel alive.
  useEffect(() => {
    if (activeId) return
    const HERO = { overview: null, ai: 'ai-mentor', trading: 'trading-terminal', ops: 'crm', wl: 'white-label' }
    const target = HERO[viewModeId]
    if (target && getModule(target)) {
      // Give the canvas a beat before auto-selecting so users can breathe.
      const t = setTimeout(() => setActiveId(target), 350)
      return () => clearTimeout(t)
    }
  }, [viewModeId, activeId])

  const resetView = () => {
    setActiveId(null)
    setHoverId(null)
    setViewModeId('overview')
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      url.hash = ''
      history.replaceState(null, '', url)
    }
  }

  const tone = TONE_COLORS[viewMode.tone] || TONE_COLORS.orange

  return (
    <section
      ref={ref}
      id="platform-architecture"
      className={clsx(
        'relative overflow-hidden py-24 md:py-32 lg:py-36 transition-opacity duration-700 scroll-mt-24',
        visible ? 'opacity-100' : 'opacity-0 translate-y-4',
      )}
    >
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.5]"
        style={{ background: 'radial-gradient(60% 45% at 50% 30%, rgba(255,193,7,0.10), transparent 70%)' }} />
      <div className="pointer-events-none absolute inset-0 -z-10"
        style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.4) 100%)' }} />
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

      <Container size="wide" className="flex flex-col gap-10 md:gap-12">
        {/* ── Header ────────────────────────────────────── */}
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Interactive · Platform Architecture</Eyebrow>
          <SectionHeading
            align="center"
            title={<>How <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">Kalki Ai Cloud</span> actually works</>}
            subtitle="A living map of every module in the platform — AI, trading, learning, billing, and white-label — connected in real time. Click any node to explore its role in the ecosystem."
          />
        </div>

        {/* ── View mode switcher ───────────────────────── */}
        <div className="flex flex-col items-center gap-3">
          <ViewModeSwitcher activeId={viewModeId} onChange={setViewModeId} />
          <div className="text-xs md:text-sm text-zinc-400 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" style={{ color: tone.hex }} />
            <span>{viewMode.description}</span>
          </div>
        </div>

        {/* ── Main triptych ────────────────────────────── */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 lg:gap-8">
          {/* Left — info */}
          <div className="xl:col-span-3 order-2 xl:order-1">
            <InfoPanel activeId={activeId} viewMode={viewMode} onSelectId={setActiveId} />
          </div>

          {/* Center — architecture canvas */}
          <div className="xl:col-span-6 order-1 xl:order-2">
            <div className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.02] to-transparent backdrop-blur-xl overflow-hidden">
              {/* Corner controls */}
              <div className="absolute right-4 top-4 z-10 flex items-center gap-2">
                <button
                  type="button"
                  onClick={resetView}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-black/50 px-3 py-1.5 text-[11px] font-semibold text-zinc-300 hover:text-white hover:bg-black/70 backdrop-blur-sm transition-all"
                  title="Reset view"
                >
                  <RotateCcw className="h-3 w-3" /> Reset
                </button>
              </div>

              {/* Bottom-left legend */}
              <div className="absolute bottom-4 left-4 z-10 flex flex-col gap-1 text-[10px] font-mono text-zinc-500">
                <div className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-brand-gold" /> Core capability</div>
                <div className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Trading layer</div>
                <div className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-violet-400" /> AI / knowledge</div>
                <div className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-sky-400" /> Business ops</div>
              </div>

              <div className="aspect-square lg:aspect-[10/9] xl:aspect-square w-full max-h-[720px] mx-auto">
                <ArchitectureCanvas
                  viewMode={viewMode}
                  activeId={activeId}
                  hoverId={hoverId}
                  onSelect={(id) => setActiveId((cur) => (cur === id ? null : id))}
                  onHover={setHoverId}
                  reducedMotion={reducedMotion}
                />
              </div>
            </div>

            {/* Below canvas — pill list of all modules (also mobile navigation) */}
            <ModulePillStrip activeId={activeId} onSelect={setActiveId} viewMode={viewMode} />
          </div>

          {/* Right — preview */}
          <div className="xl:col-span-3 order-3">
            <PreviewPanel activeId={activeId} viewMode={viewMode} />
          </div>
        </div>

        {/* ── Bottom CTA row ───────────────────────────── */}
        <div className="mt-4 md:mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 backdrop-blur-xl">
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-wider text-brand-gold font-semibold mb-1">Ready to see it live?</div>
            <h4 className="text-xl md:text-2xl font-semibold text-white leading-tight">
              A guided walkthrough of every module — in 30 minutes.
            </h4>
            <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
              Our team will map your ops to the Kalki Ai Cloud architecture and show you the shortest path to launch under your own brand.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <Link
              href="/get-started"
              className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-black transition-all hover:-translate-y-[1px]"
              style={{ background: 'linear-gradient(90deg, #FFC107, #FFE17A)', boxShadow: '0 10px 30px -10px rgba(255,193,7,0.55)' }}
            >
              Book a demo <ArrowRight className="h-4 w-4" />
            </Link>
            <GhostButton href="/platform" size="md">Explore the platform</GhostButton>
          </div>
        </div>
      </Container>
    </section>
  )
}

// ── Module pill strip — under the canvas.  On desktop it's a subtle
// legend, on mobile it becomes the primary way to switch modules.
function ModulePillStrip({ activeId, onSelect, viewMode }) {
  const highlight = viewMode.highlight === 'all' ? null : new Set(viewMode.highlight)
  return (
    <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5 pt-2">
      {MODULES.map((m) => {
        const t = TONE_COLORS[m.tone]
        const dim = highlight && !highlight.has(m.id)
        const active = activeId === m.id
        return (
          <button
            key={m.id}
            onClick={() => onSelect(active ? null : m.id)}
            className={clsx(
              'text-[10.5px] font-medium rounded-full border px-2.5 py-1 transition-all hover:-translate-y-[1px]',
              dim && 'opacity-40',
            )}
            style={{
              borderColor: active ? t.hex : `rgba(${t.rgb},0.28)`,
              color: active ? '#0a0a0a' : t.hex,
              background: active ? t.hex : `rgba(${t.rgb},0.05)`,
            }}
          >
            {m.name}
          </button>
        )
      })}
    </div>
  )
}

export default PlatformArchitecture
